import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import publicApi from "@/lib/publicApi";
import { readError } from "@/lib/api";

/**
 * Public landing for the user invite flow.
 * URL: /admin/setup-account?token=<invite_jwt>
 * Calls POST /api/auth/setup-account with {token, password}.
 */
export default function SetupAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Invalid invitation link — no token provided.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await publicApi.post("/auth/setup-account", { token, password });
      setDone(true);
    } catch (err) {
      setError(readError(err, "Could not set up account."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-card rounded-lg border border-border/60 shadow-ambient p-8">
        <div className="flex items-center gap-2 text-primary text-[11px] uppercase tracking-[0.25em] mb-3">
          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Tambure Žmegač · Invitation
        </div>
        <h1 className="font-serif text-3xl text-foreground">Set up your account</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Create a password to activate your CMS account.
        </p>
        <div className="mt-6 wood-divider w-16" />

        {done ? (
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-primary/10 border border-primary/30 p-4" data-testid="setup-success">
              <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-lg text-foreground">Account created</div>
                <p className="mt-1 text-sm text-foreground/80">You can now sign in with your email and the password you just set.</p>
              </div>
            </div>
            <Button data-testid="setup-go-login" onClick={() => navigate("/admin/login")} className="w-full h-11 shadow-interactive">
              Go to sign in
            </Button>
          </div>
        ) : (
          <form className="mt-8 space-y-5" onSubmit={submit} data-testid="setup-form">
            <div className="space-y-2">
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" required value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-background" data-testid="setup-password" />
              <p className="text-xs text-muted-foreground">At least 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2">Confirm password</Label>
              <Input id="pw2" type="password" required value={confirm} disabled={busy} onChange={(e) => setConfirm(e.target.value)} className="h-11 bg-background" data-testid="setup-confirm" />
            </div>
            {error && (
              <div data-testid="setup-error" className="flex items-start gap-2 text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
              </div>
            )}
            <Button type="submit" disabled={busy || !token} className="w-full h-11 shadow-interactive" data-testid="setup-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
